'use client';

import { useEffect, useState } from 'react';

import { Spinner } from './Spinner';
import { ApiClientError, createApiClient, type UserTransaction } from '@/lib/api';
import { formatRelative, formatUsdt } from '@/lib/format';
import { hapticImpact, getInitData } from '@/lib/tma';

export function Transactions({ refreshKey }: { refreshKey: number }) {
  const [open, setOpen] = useState(false);
  const [txns, setTxns] = useState<UserTransaction[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (txns !== null) return;
    const api = createApiClient(getInitData);
    api
      .myTransactions(50)
      .then((r) => setTxns(r.transactions))
      .catch((err) => {
        const e = err as ApiClientError;
        setError(e?.message ?? 'Не удалось загрузить транзакции');
      });
  }, [open, txns]);

  // refresh externally
  useEffect(() => {
    setTxns(null);
    setError(null);
  }, [refreshKey]);

  return (
    <div className="bg-bg-elev mb-4 border-l-[3px] border-line">
      <button
        onClick={() => {
          hapticImpact('light');
          setOpen((v) => !v);
        }}
        className="w-full p-4 flex items-center justify-between"
      >
        <span className="font-mono text-[10px] text-text-tertiary tracking-[0.15em]">
          ● ИСТОРИЯ ТРАНЗАКЦИЙ
        </span>
        <span className="text-accent display text-[18px]">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4">
          {error && <p className="text-no text-[11px]">⚠️ {error}</p>}
          {!error && !txns && (
            <div className="flex justify-center py-4">
              <Spinner size={18} />
            </div>
          )}
          {txns && txns.length === 0 && (
            <p className="font-mono text-[11px] text-text-tertiary text-center py-2 tracking-[0.05em]">
              ТРАНЗАКЦИЙ НЕТ
            </p>
          )}
          {txns && txns.length > 0 && (
            <div className="space-y-1">
              {txns.map((t) => (
                <TxnRow key={t.id} t={t} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TxnRow({ t }: { t: UserTransaction }) {
  const meta = describeTxn(t);

  return (
    <div className="flex items-start justify-between gap-2 py-2 border-b border-line/50 last:border-0">
      <div className="flex items-start gap-2.5 min-w-0">
        <span className="text-base leading-none mt-0.5">{meta.icon}</span>
        <div className="min-w-0">
          <p className="text-[12px] leading-snug">{meta.label}</p>
          <p className="font-mono text-[10px] text-text-tertiary tracking-[0.05em]">
            {formatRelative(t.created_at)}
            {t.status !== 'confirmed' && (
              <span className="ml-1.5 text-warn"> · {t.status.toUpperCase()}</span>
            )}
          </p>
        </div>
      </div>
      <span className={`display text-[13px] tabular-nums whitespace-nowrap ${meta.amountColor}`}>
        {meta.sign}
        {formatUsdt(t.amount_micro)}
      </span>
    </div>
  );
}

interface TxnMeta {
  label: string;
  icon: string;
  sign: string;
  amountColor: string;
}

function describeTxn(t: UserTransaction): TxnMeta {
  switch (t.type) {
    case 'deposit':
      return { label: `Депозит ${t.asset.replace('_', ' ')}`, icon: '⬇️', sign: '+', amountColor: 'text-yes' };
    case 'withdrawal':
      return {
        label: `Вывод${t.external_address ? ' на ' + truncate(t.external_address, 6, 4) : ''}`,
        icon: '⬆️',
        sign: '−',
        amountColor: 'text-no',
      };
    case 'bet_debit':
      return { label: 'Списание под ставку', icon: '🎯', sign: '−', amountColor: 'text-text-primary' };
    case 'bet_payout':
      return { label: 'Выплата по ставке', icon: '🏆', sign: '+', amountColor: 'text-yes' };
    case 'bet_refund':
      return { label: 'Возврат стейка (refund)', icon: '↩️', sign: '+', amountColor: 'text-text-secondary' };
    case 'referral_bonus':
      return { label: 'Реферальный бонус', icon: '🎁', sign: '+', amountColor: 'text-yes' };
    case 'promo_credit':
      return { label: 'Промо-кредит', icon: '✨', sign: '+', amountColor: 'text-yes' };
    case 'fee_collection':
      return { label: 'Комиссия оператора', icon: '💼', sign: '−', amountColor: 'text-text-tertiary' };
    default:
      return { label: t.type, icon: '◦', sign: '', amountColor: 'text-text-secondary' };
  }
}

function truncate(s: string, head: number, tail: number): string {
  if (s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
}
